// ===========================================================================
//  Site content: single source of truth.
//
//  All page copy and data live in this module. Components read from these
//  exports and hold no hardcoded text, so content changes never require
//  editing JSX.
// ===========================================================================

export const profile = {
  firstName: 'Eric',
  lastName: 'Yang',
  tagline: 'Software Engineer | MEng CS @ Cornell \'27 | BS CS @ Stevens',
  pitch: 'Software engineer building reliable backend systems, data infrastructure, and full-stack applications.',

  /**
   * Hero headshot, served from public/ at the site root.
   *
   * Hero.jsx falls back to a text-only layout if the image fails to load, so a
   * missing file degrades gracefully rather than rendering a broken image.
   * Expects a roughly square source; 600x600 is sufficient for the largest
   * render size at 2x pixel density. Set to null to disable.
   */
  photo: {
    src: '/headshot.jpg',
    alt: 'Eric Yang',
  },

  /**
   * Contact addresses, primary first.
   *
   * Contact.jsx gives the first entry the primary action button and the
   * copy-to-clipboard control, and renders any remaining entries as a
   * secondary line. The list is variable length and the component adapts.
   *
   * `label` is an optional short qualifier shown beside the address.
   */
  emails: [
    { address: 'ey294@cornell.edu', label: 'School' },
    { address: 'yhx9027@gmail.com', label: 'Work' },
  ],

  github: 'https://github.com/EricY090',
  linkedin: 'https://www.linkedin.com/in/eric-yang-4354a6250',

  // Served from public/resume.pdf at the site root.
  resumeUrl: '/resume.pdf',
};

export const fullName = `${profile.firstName} ${profile.lastName}`;

/** The address used for the main "email me" button. */
export const primaryEmail = profile.emails[0].address;

/** Anchors rendered in the sticky nav, in order. `id` must match a <Section id>. */
export const navLinks = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'now', label: 'Now' },
  { id: 'skills', label: 'Skills' },
  { id: 'contact', label: 'Contact' },
];

/** About-section body copy. Rendered as one paragraph per entry. */
export const about = [
  'Software engineer and M.Eng. Computer Science student at Cornell University, with a B.S. in Computer Science and Finance minor from Stevens Institute of Technology. Before Cornell, I worked at Yuanta Securities building production .NET applications, Python data infrastructure, and real-time systems used in quantitative trading and research.',
  'I enjoy working across the stack—from backend services and data systems to full-stack applications—and I\'m especially interested in software where reliability, performance, and thoughtful system design matter.'
];

/**
 * Experience entries, newest first.
 *   highlights: bullet list, rendered in order
 *   stack:      optional technology chips shown beneath the bullets
 *   links:      optional rich link cards, see LinkPreview.jsx
 */
export const experience = [
  {
    role: 'System Development Engineer',
    org: 'Yuanta Securities',
    period: 'July 2025 — July 2026',
    highlights: [
      'Engineered a Python market-data pipeline and alpha research system over 10+ years of tick-level exchange data across thousands of Taiwan-listed equities, powering feature generation, P&L analysis, and strategy backtesting.',
      'Cut repeated data loading by 90%+ with a config-driven data registry and metadata-based caching for the alpha research system, enabling faster research and more efficient use of compute resources.',
      'Built a .NET real-time trading-monitoring platform (NATS + Protobuf, async state handling) and reduced high-frequency redraw overhead by ~60–80% under peak market load.',
      'Shipped a 24/7 multi-process news pipeline collecting 10,000+ articles/day into MongoDB and Qdrant for semantic search',
      'Automated after-market settlement, reporting, and pre-market workflows in Python, saving roughly 30–60 minutes of manual preparation per day.',
    ],
    stack: ['Python', 'C#/.NET', 'REST APIs', 'SQL', 'NATS', 'Protobuf', 'MongoDB', 'Qdrant'],
  },
  {
    role: 'Undergraduate Researcher',
    org: 'Stevens Institute of Technology',
    period: 'June 2023 — December 2024',
    highlights: [
      'Conducted deep learning research on clinical health records across a 27M-row multimodal dataset.',
      'Developed a novel pretraining approach to improve downstream model usability, and built the data pipelines end-to-end in Python.',
      'First-author publication at IEEE BigData 2024: "MPLite: Multi-Aspect Pretraining for Mining Clinical Health Records" (DOI: 10.1109/BigData62323.2024.10825511).',
    ],
    stack: ['Python', 'Deep Learning', 'Data Analysis', 'NLP', 'TensorFlow'],
    /**
     * Rich link cards rendered beneath the bullets.
     *
     * Metadata is authored here rather than fetched at runtime: the site is
     * static, so there is no server to resolve Open Graph tags, and the
     * browser cannot request a third-party origin directly under CORS. Values
     * below come from the Crossref record for the DOI.
     */
    links: [
      {
        source: 'IEEE',
        title: 'MPLite: Multi-Aspect Pretraining for Mining Clinical Health Records',
        description:
          'Eric Yang, Pengfei Hu, Xiaoxue Han, Yue Ning — 2024 IEEE International Conference on Big Data (BigData), pp. 5096–5102.',
        url: 'https://doi.org/10.1109/BigData62323.2024.10825511',
      },
    ],
  },
  {
    role: 'Course Assistant',
    org: 'Stevens Institute of Technology',
    period: 'September 2022 — December 2022',
    highlights: [
      'Supported an introductory C programming course covering systems-level fundamentals.',
      'Ran weekly labs and office hours, helping students debug and bridging professor–student communication.',
    ],
    stack: ['C', 'Computer Architecture', 'Linux'],
  },
];


/**
 * Selected engineering projects.
 *
 * Keep these focused on projects that add a distinct technical story rather
 * than duplicating work experience. A corresponding <Section id="projects">
 * should render this data.
 */
export const projects = [
  {
    title: 'Personal Portfolio Site',
    period: 'July 2026 — August 2026',
    description:
      'This website. It was built and deployed on AWS Amplify with CloudFront, TLS, custom-domain DNS, security headers, cache-control, and Git-based CI/CD.',
    stack: ['React', 'AWS Amplify', 'CloudFront', 'Git', 'CI/CD'],
    links: [
      {
        source: 'Live Site',
        title: 'ey090.com',
        description: 'Personal portfolio and engineering profile.',
        url: 'https://ey090.com',
      },
    ],
  },
  {
    title: 'SeatMap',
    period: 'June 2025',
    description:
      'Built a full-stack seat-management application with Spring Boot, Vue 3, and MySQL, exposing RESTful APIs through a controller-service-repository architecture and using transactional seat assignment with Spring Data JPA/Hibernate.',
    stack: ['Java', 'Spring Boot', 'Vue 3', 'MySQL', 'REST APIs', 'Spring Data JPA'],
    links: [
      {
        source: 'GitHub',
        title: 'SeatMap',
        description: 'Full-stack seat-management application.',
        url: 'https://github.com/EricY090/SeatMap',
      },
    ],
  },
  {
    title: 'WangWang E-Commerce Platform',
    period: 'October 2023 — May 2024',
    description:
      'This is the project me and my friends built for our capstone project while we were undergrads. It is a full-stack e-commerce application with JavaScript, Node.js, Express, and MongoDB, including RESTful APIs, session-based authentication, and authorization middleware.',
    stack: ['JavaScript', 'Node.js', 'Express', 'MongoDB', 'REST APIs'],
    links: [
      {
        source: 'GitHub',
        title: 'WangWang Website',
        description: 'Full-stack e-commerce application.',
        url: 'https://github.com/EricY090/WangWangWebsite',
      },
    ],
  },
];

/**
 * Current and upcoming work.
 *
 * Maintenance note: this is the only section whose copy references a specific
 * academic term, so it needs review each semester. Setting `now` to null hides
 * the section, in which case the matching `{ id: 'now' }` entry must also be
 * removed from navLinks, or the nav will anchor to an element that no longer
 * exists.
 *
 *   items[].status: short label rendered as a pill
 *   items[].links:  same shape as the experience links above
 *   courses:        optional, rendered as a labelled chip row
 *   involvement:    optional, rendered as a list
 *   offTheClock:    optional, rendered as a list
 *
 * The three optional blocks are independently nullable.
 */
export const now = {
  eyebrow: 'Now',
  title: "What I'm up to this fall",
  intro:
    "Currently pursuing my M.Eng. in Computer Science at Cornell. Between coursework, engineering projects, and campus involvement, here's what I'm working on this fall.",

  // Active projects, rendered as cards.
  items: [
    {
      title: 'AI on Azure',
      status: 'In progress',
      description:
        'Deploying an open-source Llama model on Azure behind a containerized FastAPI service and lightweight chat UI, exploring cloud provisioning, inference infrastructure, and cost-aware deployment.',
      stack: ['Azure', 'Docker', 'FastAPI', 'Python'],
    },
    {
      title: 'Contributing to nats.py',
      status: 'Planned',
      description:
        "I relied on the NATS Python client while building trading infrastructure, and reading its internals taught me a great deal about async Python. I plan to contribute back by working directly with a library that production systems depend on.",
      stack: ['Python', 'NATS'],
      // Populate `links` with the pull request once one is open:
      // links: [{ source: 'GitHub', title: '...', description: '...', url: '...' }],
    },
  ],

  /** Current-term coursework. Set to null to hide this block. */
  courses: {
    term: 'Fall 2026',
    items: [
      { code: 'CS 5414', name: 'Distributed Computing Principles' },
      { code: 'CS 5154', name: 'Software Testing' },
      { code: 'CS 5700', name: 'Foundations of AI Reasoning and Decision-Making' },
      { code: 'CS 7090', name: 'Computer Science Colloquium' },
      { code: 'NBA 5070', name: 'Entrepreneurship for Scientists & Engineers' },
    ],
  },

  /** Campus organizations. Set to null or an empty array to hide this block. */
  involvement: [
    { name: 'CS Graduate Organization (CGSO)'},
    { name: 'Cornell Data Science' },
  ],

  /** Personal interests. Set to null or an empty array to hide this block. */
  offTheClock: [
    'Chasing a better pour-over and scouting every bubble-tea shop within walking distance of campus.',
    "Getting out onto Ithaca's gorges and Finger Lakes trails whenever the weather cooperates.",
    'Rock climbing sometimes in NYC, and losing the occasional game of Chess with as much grace as I can manage.',
    'Still reading about markets and quant finance — some habits from the trading desk die hard.',
  ],
};

/**
 * Skill groups. `icon` is a lucide-react export name; Skills.jsx resolves it
 * through a lookup table, so a new icon must also be imported there.
 */
export const skills = [
  {
    group: 'Languages',
    icon: 'Code2',
    items: ['Python', 'C#', 'Java', 'JavaScript', 'SQL'],
  },
  {
    group: 'Frameworks & Tools',
    icon: 'Wrench',
    items: ['.NET', 'Node.js', 'React', 'Spring Boot', 'Spring Data JPA', 'FastAPI', 'Docker', 'Git', 'NATS'],
  },
  {
    group: 'Databases',
    icon: 'Database',
    items: ['MongoDB', 'MySQL', 'SQL Server', 'PostgreSQL', 'Qdrant'],
  },
  {
    group: 'Cloud',
    icon: 'Cloud',
    items: ['AWS', 'Azure', 'Amplify', 'CloudFront', 'Cloudflare DNS'],
  },
];

/** Closing note rendered beneath the skills grid. Set to null to remove it. */
export const skillsNote = {
  lead: 'Always learning.',
  body: "This list keeps growing as I pick up new tools quickly and enjoy it!",
};

export const footer = {
  year: new Date().getFullYear(),
  builtWith: 'Built with React & Tailwind · Deployed on AWS Amplify',
};

// The page <title>, meta description, and Open Graph tags are static markup in
// public/index.html. CRA cannot read this module at build time, so those values
// must be kept in sync manually when the name or pitch changes.